/**
 * Automation Helper - Types and Constants
 * @author Muskan Thakur
 */
import { addDays, addMonths, addWeeks, addYears, format, isValid, parseISO } from "date-fns";
import { label, number } from "../../config";
import { getUsersForDynamicGroup } from "../Admin/admin.service";
import { buildDefaultFormSchema } from "../Forms/constants/formSchema.constants";
import { getRecurrTitle, getRecurrTitleCompletionBased } from "../Tasks/RecurringTask/recurringTaskHelper";

/**
 * Helper function to check if an ID is new (unsaved)
 */
export const isNewId = (id) => {
  if (!id || id === null || id === undefined) return true;
  const idStr = id.toString();
  if (idStr.startsWith("temp-")) return true;
  const numId = parseInt(idStr, 10);
  return isNaN(numId) || numId <= 0;
};

/**
 * Helper function to check if an ID is saved (has a valid numeric ID)
 */
export const isSavedId = (id) => {
  if (!id || id === null || id === undefined) return false;
  const idStr = id.toString();
  if (idStr.startsWith("temp-")) return false;
  const numId = parseInt(idStr, 10);
  return !isNaN(numId) && numId > 0;
};

/** Stable key for last-saved trigger update payload maps  */
export const getTriggerPayloadMapKey = (triggerId) => {
  if (triggerId == null || triggerId === "") return null;
  return String(triggerId);
};

/** Returns true when the group is dynamic (API may send boolean or 1/0). */
export const isDynamicOrStaticGroup = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  return false;
};

/**
 * Used to block manual trigger until members exist.  */
export const isAutomationAudienceEmptyForTrigger = async (automation, user) => {
  const audience = automation?.audience;
  if (!audience?.selectedGroup) return false;

  if (isDynamicOrStaticGroup(audience.selectedGroup.isDynamic) && user?.companyId) {
    try {
      const dynamicUsers = await getUsersForDynamicGroup({
        companyId: user.companyId,
        groupId: audience.groupId,
      });
      return !dynamicUsers || dynamicUsers.length === 0;
    } catch {
      return true;
    }
  }

  return audience.userCount === 0;
};

/**
 * Utility function to format date consistently using date-fns
 */
export const formatDate = (date, formatString = "yyyy-MM-dd") => {
  if (!date) return null;

  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return null;
    return format(dateObj, formatString);
  } catch (error) {
    return null;
  }
};

/**
 * Utility function to get today's date in consistent format
 */
export const getTodayDate = (formatString = "yyyy-MM-dd") => {
  return format(new Date(), formatString);
};

/**
 * Utility function to validate date string
 */
export const isValidDateString = (dateString) => {
  if (!dateString) return false;
  try {
    return isValid(parseISO(dateString));
  } catch (error) {
    return false;
  }
};

/**
 * Gets the workflow name for a trigger
 * @author Muskan Thakur
 */
export const getWorkflowName = (trigger, workflows = []) => {
  if (!trigger) return null;

  if (trigger.AutomationWorkflowId && trigger.WorkflowId && trigger.AutomationWorkflowId === trigger.WorkflowId) {
    return trigger.WorkflowName || null;
  }

  if (!workflows.length) {
    return trigger.WorkflowName || null;
  }

  const formId = trigger.FormId ?? trigger.formId;
  const hasFormId = formId != null && formId !== "";
  const workflowId = trigger.WorkflowId ?? trigger.workflowId;

  const workflow = workflows.find((w) =>
    hasFormId
      ? w.formId != null && String(w.formId) === String(formId)
      : workflowId != null &&
        ((w.WorkflowId != null && String(w.WorkflowId) === String(workflowId)) || (w.Id != null && String(w.Id) === String(workflowId)))
  );

  return workflow ? workflow.WorkflowName : (trigger.WorkflowName || null);
};

/**
 * Synchronizes trigger keys to ensure consistency between local and API formats
 */
/**
 * Maps API / create-response trigger fields onto the PascalCase shape the UI expects.
 * Prevents a truthy-but-empty TriggerDetails object from becoming a one-element array of `{}`.
 */
export const normalizeAutomationTriggerShape = (trigger) => {
  if (!trigger || typeof trigger !== "object") {
    return null;
  }
  const TriggerId = trigger.TriggerId ?? trigger.Id ?? trigger.triggerId;
  const TriggerName = trigger.TriggerName ?? trigger.Name ?? trigger.triggerName;
  return {
    ...trigger,
    ...(TriggerId != null && String(TriggerId) !== "" ? { TriggerId } : {}),
    ...(TriggerName != null && String(TriggerName) !== "" ? { TriggerName } : {}),
  };
};

/** Child task type keys used in automation Trigger Config (WorkflowTab). */
export const CHILD_TASK_TYPE_KEY = {
  SIMPLE_CHECKLIST: "simpleChecklist",
  DESCRIPTIVE_INPUT: "descriptiveInput",
  FORM: "form",
};

/** API `TriggerTypes[].Name` (lowercase) → child task type key; aligns with getAutomationDetails `TriggerTypes`. */
const TRIGGER_TYPE_NAME_TO_CHILD_TASK_KEY = {
  checklist: CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST,
  descriptive: CHILD_TASK_TYPE_KEY.DESCRIPTIVE_INPUT,
  form: CHILD_TASK_TYPE_KEY.FORM,
};

const pickTriggerTypeId = (row) => row?.Id ?? row?.id;
const pickTriggerTypeName = (row) => row?.Name ?? row?.name;
const normalizeTriggerTypeNameFromRow = (row) => String(pickTriggerTypeName(row) ?? "").trim().toLowerCase();

const getAutomationTriggerTypesList = (automation) => {
  const list = automation?.triggerTypes;
  return Array.isArray(list) ? list : [];
};

const childTaskKeyFromTriggerTypeRow = (row) => {
  const n = pickTriggerTypeName(row);
  if (n == null || String(n).trim() === "") return null;
  const normalized = String(n).trim().toLowerCase();
  return TRIGGER_TYPE_NAME_TO_CHILD_TASK_KEY[normalized] ?? null;
};

/** Fallback when `TriggerTypes` is missing (e.g. new automation before details load). */
const CHILD_TASK_KEY_TO_TRIGGER_TYPE_FIELDS = {
  [CHILD_TASK_TYPE_KEY.DESCRIPTIVE_INPUT]: { TriggerType: 1, TriggerTypeName: "Descriptive", isSimpleChecklist: false },
  [CHILD_TASK_TYPE_KEY.FORM]: { TriggerType: 2, TriggerTypeName: "Form", isSimpleChecklist: false },
  [CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST]: { TriggerType: 3, TriggerTypeName: "Checklist", isSimpleChecklist: true },
};

/**
 * Maps trigger API fields to child task type key for UI selection.
 * When `automation` includes `triggerTypes` from getAutomationDetails, uses that catalog (Id / Name) before numeric fallbacks.
 */
export const getChildTaskTypeKeyFromTrigger = (trigger, automation) => {
  if (!trigger || typeof trigger !== "object") {
    return CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST;
  }

  const triggerTypes = getAutomationTriggerTypesList(automation);

  const typeNameRaw = trigger.TriggerTypeName ?? trigger.triggerTypeName;
  if (typeNameRaw != null && String(typeNameRaw).trim() !== "") {
    const normalized = String(typeNameRaw).trim().toLowerCase();
    const fromStaticMap = TRIGGER_TYPE_NAME_TO_CHILD_TASK_KEY[normalized];
    if (fromStaticMap) return fromStaticMap;
    if (triggerTypes.length > 0) {
      const row = triggerTypes.find((t) => normalizeTriggerTypeNameFromRow(t) === normalized);
      const fromApi = row && childTaskKeyFromTriggerTypeRow(row);
      if (fromApi) return fromApi;
    }
  }

  const typeNum = trigger.TriggerType ?? trigger.triggerType;
  if (typeNum != null && typeNum !== "" && !Number.isNaN(Number(typeNum))) {
    const n = Number(typeNum);
    if (triggerTypes.length > 0) {
      const row = triggerTypes.find((t) => Number(pickTriggerTypeId(t)) === n);
      const fromApi = row && childTaskKeyFromTriggerTypeRow(row);
      if (fromApi) return fromApi;
    }
    if (n === 1) return CHILD_TASK_TYPE_KEY.DESCRIPTIVE_INPUT;
    if (n === 2) return CHILD_TASK_TYPE_KEY.FORM;
    if (n === 3) return CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST;
  }

  const formId = trigger.FormId ?? trigger.formId;
  if (formId != null && formId !== "") {
    return CHILD_TASK_TYPE_KEY.FORM;
  }

  if (trigger.isSimpleChecklist === false) {
    return CHILD_TASK_TYPE_KEY.DESCRIPTIVE_INPUT;
  }

  return CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST;
};

/**
 * Whether a row from getAutomationInstance belongs to a Form child-task trigger,
 * using `Triggers` from getAutomationDetails for that automation.
 */
export const isAutomationInstanceFormTrigger = (dataItem, triggersByAutomationId) => {
  if (!dataItem?.AutomationId || !triggersByAutomationId || typeof triggersByAutomationId !== "object") {
    return false;
  }
  const triggers = triggersByAutomationId[dataItem.AutomationId];
  if (!Array.isArray(triggers) || triggers.length === 0) return false;

  const rowWorkflowId = dataItem.WorkflowId ?? dataItem.workflowId;
  const rowTriggerName = dataItem.TriggerName ?? dataItem.triggerName;

  const match = triggers.find((t) => {
    const wf = t.WorkflowId ?? t.workflowId;
    const name = t.TriggerName ?? t.triggerName;
    return (
      wf != null &&
      rowWorkflowId != null &&
      String(wf) === String(rowWorkflowId) &&
      name != null &&
      rowTriggerName != null &&
      String(name).trim() === String(rowTriggerName).trim()
    );
  });

  if (!match) return false;
  return getChildTaskTypeKeyFromTrigger(match) === CHILD_TASK_TYPE_KEY.FORM;
};

export const getTriggerTypeFieldsForChildTaskKey = (taskKey, automation) => {
  const triggerTypes = getAutomationTriggerTypesList(automation);
  if (triggerTypes.length > 0) {
    const invertedEntry = Object.entries(TRIGGER_TYPE_NAME_TO_CHILD_TASK_KEY).find(([, v]) => v === taskKey);
    if (invertedEntry) {
      const needle = invertedEntry[0];
      const row = triggerTypes.find((t) => normalizeTriggerTypeNameFromRow(t) === needle);
      const id = row != null ? pickTriggerTypeId(row) : null;
      if (id != null && !Number.isNaN(Number(id))) {
        const displayName = pickTriggerTypeName(row);
        const fallback = CHILD_TASK_KEY_TO_TRIGGER_TYPE_FIELDS[taskKey];
        return {
          TriggerType: Number(id),
          TriggerTypeName:
            displayName != null && String(displayName).trim() !== "" ? String(displayName).trim() : fallback.TriggerTypeName,
          isSimpleChecklist: taskKey === CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST,
        };
      }
    }
  }
  return CHILD_TASK_KEY_TO_TRIGGER_TYPE_FIELDS[taskKey] ?? CHILD_TASK_KEY_TO_TRIGGER_TYPE_FIELDS[CHILD_TASK_TYPE_KEY.SIMPLE_CHECKLIST];
};

/**
 * Numeric TriggerType for create/update trigger APIs.
 * Uses trigger.TriggerType when set; otherwise TriggerTypeName resolved via getAutomationDetails `TriggerTypes` (Id / Name);
 * then {@link getChildTaskTypeKeyFromTrigger} with the same catalog; then legacy field fallbacks.
 */
export const resolveTriggerTypeForApi = (trigger, automation) => {
  const explicit = trigger?.TriggerType ?? trigger?.triggerType;
  if (explicit != null && explicit !== "" && !Number.isNaN(Number(explicit))) {
    return Number(explicit);
  }

  const typeNameRaw = trigger?.TriggerTypeName ?? trigger?.triggerTypeName;
  const triggerTypes = getAutomationTriggerTypesList(automation);
  if (typeNameRaw != null && String(typeNameRaw).trim() !== "" && triggerTypes.length > 0) {
    const normalized = String(typeNameRaw).trim().toLowerCase();
    const match = triggerTypes.find((t) => normalizeTriggerTypeNameFromRow(t) === normalized);
    const id = match != null ? pickTriggerTypeId(match) : null;
    if (id != null && !Number.isNaN(Number(id))) {
      return Number(id);
    }
  }

  const key = getChildTaskTypeKeyFromTrigger(trigger, automation);
  return getTriggerTypeFieldsForChildTaskKey(key, automation).TriggerType;
};

/** Backend `IsRecurrenceDisabled` / `isRecurrenceDisabled`: 1 = disabled, 0 = enabled. */
export const getIsRecurrenceDisabledFromTrigger = (trigger) => {
  const v = trigger?.IsRecurrenceDisabled ?? trigger?.isRecurrenceDisabled;
  if (v !== undefined && v !== null) {
    return v;
  }
  if (trigger?.recurrenceContext?.recurrenceEnabled !== undefined) {
    return trigger.recurrenceContext.recurrenceEnabled ? 0 : 1;
  }
  return 1;
};

export const synchronizeTriggerKeys = (trigger) => {
  if (!trigger) return trigger;

  const currentTriggerPattern = trigger.recurrenceContext?.recurrencePattern || trigger.TriggerPattern || 1;
  const currentEndCriteria = trigger.recurrenceContext?.duration?.value || trigger.EndCriteria || 1;

  let frequencyTypeDate = null;
  let frequencyTypeDuration = null;
  let frequencyDay = null;

  if (currentTriggerPattern === 1) {
    frequencyTypeDate = trigger.recurrenceContext?.recur?.value || trigger.FrequencyTypeDate || 1;
    frequencyTypeDuration = null;
    frequencyDay = null;
  } else if (currentTriggerPattern === 2) {
    frequencyTypeDate = null;
    frequencyTypeDuration = trigger.recurrenceContext?.recur?.value || trigger.FrequencyTypeDuration || 1;

    frequencyDay = trigger.recurrenceContext?.skipCountValue || 3;
  }

  const repeatCount = currentEndCriteria === 3 ? trigger.recurrenceContext?.endByCountValue || trigger.RepeatCount || 1 : null;

  const requiredKeys = {
    AutomationId: trigger.AutomationId,
    TriggerId: trigger.TriggerId,
    TriggerName: trigger.TriggerName,
    WorkflowId: trigger.WorkflowId,
    TriggerAssignee: trigger.TriggerAssignee,
    TriggerPattern: currentTriggerPattern,
    FrequencyTypeDate: frequencyTypeDate,
    FrequencyTypeDuration: frequencyTypeDuration,
    FrequencyDay: frequencyDay,
    EndCriteria: currentEndCriteria,
    RepeatCount: repeatCount,
    StartDate: trigger.startDate,
    EndDate: trigger.EndDate,
    isDisabled: trigger.IsDisabled,
    IsRecurrenceDisabled: getIsRecurrenceDisabledFromTrigger(trigger),
    CreatedBy: trigger.CreatedBy,
    CreatedOn: trigger.CreatedOn,
    LastTriggeredAt: trigger.LastTriggeredAt,
    DueIn: trigger.DueIn,
    WorkflowName: trigger.WorkflowName,
    AutomationWorkflowId: trigger.AutomationWorkflowId,
    AutomationWorkflowName: trigger.AutomationWorkflowName,
  };

  return { ...trigger, ...requiredKeys };
};

/**
 * Generates default automation name with current date
 * @author Muskan Thakur
 */
export const getDefaultAutomationName = () => {
  try {
    const today = new Date();
    const formattedDate = format(today, "dd MMM yyyy");
    return `Automation ${formattedDate}`;
  } catch (error) {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[today.getMonth()];
    const year = today.getFullYear();
    return `Automation ${day} ${month} ${year}`;
  }
};

/**
 * Creates a selectedGroup object for automation audience
 * @author Muskan Thakur
 */
export const createSelectedGroup = ({
  groupId,
  name,
  description,
  userCount,
  users = [],
  user,
  teamId,
  icon = "groups",
  isDynamic = false,
}) => {
  return {
    id: groupId,
    name,
    description,
    type: "user",
    userCount,
    users,
    createdBy: user?.id,
    createdByName: user?.name,
    icon,
    isDynamic,
    isAdmin: false,
    companyId: user?.companyId,
    teamId,
    isActive: true,
  };
};

/** Base Automation interface */
export const createAutomationInterface = () => {
  try {
    const defaultName = getDefaultAutomationName();

    return {
      id: null,
      name: defaultName,
    quickInfo: "",
  status: "active",
  teamCode: "",
  workspaceName: "",
  startDate: null,
  endDate: null,
  createdBy: null,
  createdAt: null,
  updatedAt: null,
  triggers: [],
  audience: {
    type: "preset",
    groupId: null,
    selectedGroup: null,
    customGroup: {
      name: "",
      description: "",
      members: [],
      filters: {
        jobRole: "",
        department: "",
        officeLocation: "",
        team: "",
      },
    },
    users: [],
    userCount: 0,
  },
  activeTriggersCount: 0,
  icon: null,
  teamId: null,
  isDisabled: false,
  automationAudience: null,
  dynamicVariableIds: null,
  dynamicVariableValues: null,
    };
  } catch (error) {
    const defaultName = getDefaultAutomationName();

    return {
      id: null,
      name: defaultName,
      quickInfo: "",
      status: "active",
      teamCode: "",
      workspaceName: "",
      startDate: null,
      endDate: null,
      createdBy: null,
      createdAt: null,
      updatedAt: null,
      triggers: [],
      audience: {
        type: "preset",
        groupId: null,
        selectedGroup: null,
        customGroup: {
          name: "",
          description: "",
          members: [],
          filters: {
            jobRole: "",
            department: "",
            officeLocation: "",
            team: "",
          },
        },
        users: [],
        userCount: 0,
      },
      activeTriggersCount: 0,
      icon: null,
      teamId: null,
      isDisabled: false,
      automationAudience: null,
      dynamicVariableIds: null,
      dynamicVariableValues: null,
    };
  }
};

/** Character limits */
export const CHARACTER_LIMITS = {
  AUTOMATION_NAME: 90,
  QUICK_INFO: 90,
  GROUP_NAME: 30,
  GROUP_DESCRIPTION: 90,
};

/** Audience types */
export const AUDIENCE_TYPES = {
  PRESET: "preset",
  CUSTOM: "custom",
};

/** User selection types */
export const USER_SELECTION_TYPES = {
  DYNAMIC: "dynamic",
  STATIC: "static",
};

/**
 * Generates custom verbiage for automation frequency based on trigger data
 * @author Muskan Thakur
 */
export const getAutomationFrequencyTitle = (trigger) => {
  if (!trigger) return label.FREQUENCY_NOT_CONFIGURED;
  const { TriggerPattern, FrequencyTypeDate, FrequencyDay, EndCriteria, RepeatCount, EndDate, StartDate, FrequencyTypeDuration } = trigger;
  const isDateBased = TriggerPattern === number.ONE;
  const isDurationBased = TriggerPattern === number.TWO;
  if (!isDateBased && !isDurationBased) {
    return label.FREQUENCY_NOT_CONFIGURED;
  }
  const recur = isDateBased ? FrequencyTypeDate : FrequencyTypeDuration;
  const duration = EndCriteria;
  const skipCountValue = FrequencyDay;
  const remainingRecurrence = RepeatCount;
  const frequencyDayDate = StartDate ? new Date(StartDate) : new Date();
  const frequencyInterval = null;
  const frequencyIntervalType = null;
  const weeklyCustomRecurDay = null;
  const monthlyCustomRecurDates = null;
  let frequencyDescription = "";
  if (isDateBased) {
    frequencyDescription = getRecurrTitle(
      recur,
      duration,
      remainingRecurrence,
      EndDate,
      frequencyDayDate,
      frequencyInterval,
      frequencyIntervalType,
      weeklyCustomRecurDay,
      monthlyCustomRecurDates
    );
  } else if (isDurationBased) {
    frequencyDescription = getRecurrTitleCompletionBased(recur, duration, skipCountValue, remainingRecurrence, EndDate);
  }
  if (frequencyDescription) {
    frequencyDescription = frequencyDescription
      .replace(/This Task recurs/gi, "Recurs")
      .replace(/The task recurs/gi, "Recurs")
      .replace(/This task recurs/gi, "Recurs");
  }
  return frequencyDescription || label.FREQUENCY_NOT_CONFIGURED;
};

/** Parse trigger start/end inputs into a valid Date or null (avoids Invalid Date breaking date-fns add*) */
const toValidDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  if (typeof value === "string") {
    let parsed = parseISO(value);
    if (!isValid(parsed)) {
      parsed = new Date(value);
    }
    return isValid(parsed) ? parsed : null;
  }
  const d = new Date(value);
  return isValid(d) ? d : null;
};

const safeInt = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.trunc(x);
};

/**
 * Calculates the end date for automation triggers based on recurrence settings
 * @author Muskan Thakur
 */
export const calculateAutomationEndDate = (recurrenceContext, startDate) => {
  if (!recurrenceContext || startDate == null || startDate === "") return null;

  const { recur, duration, endBydateValue, endByCountValue, recurrencePattern, skipCountValue } = recurrenceContext;

  if (!duration || !duration.value) return null;

  if (duration.value === 2 && endBydateValue) {
    const d = toValidDate(endBydateValue);
    if (!d) return null;
    return format(d, "yyyy-MM-dd");
  }

  if (duration.value === 3 && endByCountValue && endByCountValue > 0) {
    const endDateBase = toValidDate(startDate);
    if (!endDateBase) return null;

    const skip = safeInt(skipCountValue);
    const count = safeInt(endByCountValue);
    if (count <= 0) return null;

    let endDate = endDateBase;

    if (recurrencePattern === 2) {
      switch (recur?.value) {
        case 1:
          endDate = addDays(endDate, skip * count);
          break;
        case 2:
          endDate = addWeeks(endDate, skip * count);
          break;
        case 3:
          endDate = addMonths(endDate, skip * count);
          break;
        case 4:
          endDate = addYears(endDate, skip * count);
          break;
        default:
          return null;
      }
    } else {
      switch (recur?.value) {
        case 1:
          endDate = addDays(endDate, count);
          break;
        case 2:
          endDate = addWeeks(endDate, count - 1);
          break;
        case 3:
          endDate = addMonths(endDate, count - 1);
          break;
        case 4:
          endDate = addYears(endDate, count - 1);
          break;
        case 6:
          endDate = addMonths(endDate, count * 3);
          break;
        default:
          return null;
      }
    }

    if (!isValid(endDate)) return null;
    return format(endDate, "yyyy-MM-dd");
  }

  if (duration.value === 1) {
    return null;
  }

  return null;
};

/**
 * Parent/child task name tags → comma-separated chip ids for trigger APIs.
 */
export const convertTaskNameTagsToConfigString = (tags, taskNameChips) => {
  if (!tags || !Array.isArray(tags) || !taskNameChips || !Array.isArray(taskNameChips)) {
    return null;
  }
  const chipIds = tags
    .filter((tag) => tag.type !== "TEXT")
    .map((tag) => {
      const chip = taskNameChips.find((c) => c.ChipName === tag.type || c.ChipName === tag.value);
      return chip ? chip.Id : null;
    })
    .filter((id) => id != null);
  return chipIds.length > 0 ? chipIds.join(",") : null;
};

/** Calendar date yyyy-MM-dd for API payloads and last-saved comparisons (avoids ISO vs date-only false diffs). */
const toTriggerPayloadDateString = (value) => {
  if (value == null || value === "") {
    return null;
  }
  try {
    if (value instanceof Date && isValid(value)) {
      return format(value, "yyyy-MM-dd");
    }
    const s = String(value).trim();
    /** Wire dates often include time (ISO); calendar day is first 10 chars — avoids local TZ shifting the day vs last saved payload. */
    const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymd) {
      return ymd[1];
    }
    if (isValidDateString(s)) {
      const d = parseISO(s);
      return isValid(d) ? format(d, "yyyy-MM-dd") : null;
    }
  } catch {
    /* ignore */
  }
  return null;
};

/**
 * Payload for POST /workflow/addWorkflowForm when linking a default form to an automation schedule.
 * Mirrors AddFormPopup / FormBuilder add shape; adds workflowId + automationTriggerId for server linkage.
 */
export function buildAddWorkflowFormPayloadForAutomationTrigger({
  workflowId,
  automationTriggerId,
  triggerName,
  description = "",
  user,
  projectId,
}) {
  const title = (triggerName && String(triggerName).trim()) || "Form";
  const content = buildDefaultFormSchema(title, description || "");
  const isMyBoard = user?.myProjectId != null && projectId != null && String(projectId) === String(user.myProjectId);

  return {
    workflowId: workflowId ?? null,
    automationTriggerId: automationTriggerId ?? null,
    AutomationTriggerId: automationTriggerId ?? null,
    content,
    title,
    description: description || "",
    companyId: user?.companyId,
    userId: user?.id,
    projectId,
    privacy: isMyBoard ? number.THREE : number.TWO,
  };
}

/**
 * Full payload for updateAutomationTrigger (schedule, recurrence flag, task name chips, etc.).
 */
export const buildUpdateAutomationTriggerPayload = (trigger, automation, userId) => {
  const rc = trigger.recurrenceContext;
  /** Match TriggerSection save merge: schedule calendar wins so last saved vs current payload agree on startDate. */
  const calendarStart =
    rc?.calenderValue ??
    rc?.calendarValue ??
    null;
  let startDateRaw = calendarStart ?? trigger.StartDate ?? trigger.startDate;
  let startDate = toTriggerPayloadDateString(startDateRaw);
  if (!startDate) {
    startDate = getTodayDate();
  }

  let endDate = trigger.EndDate ?? trigger.endDate;
  if (rc) {
    endDate = calculateAutomationEndDate(rc, new Date(startDate));
  }
  endDate = endDate != null && endDate !== "" ? toTriggerPayloadDateString(endDate) ?? endDate : null;

  const pattern = trigger.TriggerPattern ?? trigger.triggerPattern ?? 1;
  /** Keep frequency defaults aligned with synchronizeTriggerKeys so last saved vs save diffs stay stable. */
  let frequencyTypeDate = null;
  let frequencyTypeDuration = null;
  if (pattern === 1) {
    frequencyTypeDate = rc?.recur?.value || trigger.FrequencyTypeDate || 1;
    frequencyTypeDuration = null;
  } else if (pattern === 2) {
    frequencyTypeDate = null;
    frequencyTypeDuration = rc?.recur?.value || trigger.FrequencyTypeDuration || 1;
  }

  const endCriteria = trigger.EndCriteria;
  const repeatCount = endCriteria === 3 ? rc?.endByCountValue || trigger.RepeatCount || 1 : null;
  const chips = automation?.taskNameChips;

  const triggerTypeNumRaw = resolveTriggerTypeForApi(trigger, automation);
  const triggerTypeNum = Number.isFinite(Number(triggerTypeNumRaw)) ? Number(triggerTypeNumRaw) : 3;

  const formIdVal = trigger.FormId ?? trigger.formId;
  const hasFormId = formIdVal != null && formIdVal !== "";

  const payload = {
    triggerId: trigger.TriggerId,
    updatedBy: userId,
    assignee: trigger.TriggerAssignee,
    workflowId: hasFormId ? trigger.workflowId : trigger.WorkflowId,
    triggerType: triggerTypeNum,
    startDate,
    triggerPattern: pattern,
    frequencyTypeDate,
    frequencyTypeDuration,
    frequencyDay: pattern === 1 ? null : rc?.skipCountValue || trigger.FrequencyDay || 3,
    endCriteria,
    endDate,
    skipDays: trigger.SkipDays,
    repeatCount,
    isRecurrenceDisabled: getIsRecurrenceDisabledFromTrigger(trigger),
    dueIn: trigger.DueIn ?? trigger.dueIn ?? null,
    priority: trigger.Priority ?? trigger.priority ?? null,
    parentTaskNameConfig: convertTaskNameTagsToConfigString(trigger.ParentTaskNameTags, chips),
    childTaskNameConfig: convertTaskNameTagsToConfigString(trigger.ChildTaskNameTags, chips),
  };

  if (hasFormId) {
    payload.formId = formIdVal;
  }

  return serializeUpdateAutomationTriggerForApi(payload);
};

/**
 * Loose equality for comparing two serialized updateAutomationTrigger field values.
 * @param {string} [key] — field name for date / numeric normalization
 */
export const valuesEqualTriggerUpdateField = (a, b, key = null) => {
  if (Object.is(a, b)) return true;
  if (key === "startDate" || key === "endDate") {
    const ca = toTriggerPayloadDateString(a) ?? (a == null || a === "" ? null : String(a));
    const cb = toTriggerPayloadDateString(b) ?? (b == null || b === "" ? null : String(b));
    return ca === cb;
  }
  if (key === "isRecurrenceDisabled") {
    return a === b;
  }
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === "object" || typeof b === "object") {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  }
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  if (typeof a === "boolean" || typeof b === "boolean") {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
};

/**
 * Removes empty `formId` / `FormId` from a trigger API body (add or update).
 * @param {Record<string, unknown>} out — mutable object (typically a shallow copy of the payload)
 */
function omitEmptyFormIdKeys(out) {
  if (!out || typeof out !== "object") {
    return out;
  }
  if (out.formId == null || out.formId === "") {
    delete out.formId;
  }
  if (out.FormId == null || out.FormId === "") {
    delete out.FormId;
  }
  return out;
}

/**
 * Normalizes addAutomationTrigger request body before the API call.
 */
export const serializeAddAutomationTriggerForApi = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  return omitEmptyFormIdKeys({ ...payload });
};

/**
 * Normalizes any partial/full updateAutomationTrigger body before the API call:
 * numeric `triggerType`, strip `TriggerType`, omit empty `formId` / `FormId`.
 */
export const serializeUpdateAutomationTriggerForApi = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  const out = { ...payload };
  const typeVal = out.triggerType ?? out.TriggerType;
  if (typeVal != null && typeVal !== "") {
    const n = Number(typeVal);
    if (Number.isFinite(n)) {
      out.triggerType = n;
    }
  }
  delete out.TriggerType;
  return omitEmptyFormIdKeys(out);
};

/**
 * Minimal updateAutomationTrigger body: always triggerId; updatedBy only when at least one other field changed.
 * If `lastSavedPayload` is missing, returns a full copy of `currentPayload` (nothing persisted yet to diff against).
 */
export const diffUpdateAutomationTriggerPayload = (lastSavedPayload, currentPayload) => {
  if (!currentPayload || currentPayload.triggerId == null) {
    return serializeUpdateAutomationTriggerForApi(currentPayload);
  }
  if (!lastSavedPayload) {
    return serializeUpdateAutomationTriggerForApi({ ...currentPayload });
  }

  const out = { triggerId: currentPayload.triggerId };
  let hasChanges = false;
  for (const key of Object.keys(currentPayload)) {
    if (key === "triggerId" || key === "updatedBy") {
      continue;
    }
    if (valuesEqualTriggerUpdateField(lastSavedPayload[key], currentPayload[key], key)) {
      continue;
    }
    out[key] = currentPayload[key];
    hasChanges = true;
  }
  if (hasChanges && currentPayload.updatedBy != null && currentPayload.updatedBy !== "") {
    out.updatedBy = currentPayload.updatedBy;
  }
  return serializeUpdateAutomationTriggerForApi(
    hasChanges ? out : { triggerId: currentPayload.triggerId }
  );
};

/**
 * After loading automation from the server, builds the last **saved** `updateAutomationTrigger` API payload per trigger id
 * (what we last treated as in sync). Used with {@link diffUpdateAutomationTriggerPayload} so saves send only changed fields.
 */
export const buildLastSavedTriggerUpdatePayloadMap = (automation, userId) => {
  if (!automation?.id) {
    return { automationId: null, lastSavedPayloadByTriggerId: {} };
  }
  if (!userId || !Array.isArray(automation.triggers)) {
    return { automationId: automation.id, lastSavedPayloadByTriggerId: {} };
  }

  const lastSavedPayloadByTriggerId = {};
  for (const raw of automation.triggers) {
    if (!raw || typeof raw !== "object") continue;
    const normalized = normalizeAutomationTriggerShape(raw);
    const t = synchronizeTriggerKeys(normalized ?? raw);
    if (!t?.TriggerId || isNewId(t.TriggerId)) continue;
    const lastSavedPayload = buildUpdateAutomationTriggerPayload(t, automation, userId);
    if (t.TriggerName != null) {
      lastSavedPayload.name = t.TriggerName;
    }
    const triggerKey = getTriggerPayloadMapKey(t.TriggerId);
    if (triggerKey) {
      lastSavedPayloadByTriggerId[triggerKey] = lastSavedPayload;
    }
  }
  return { automationId: automation.id, lastSavedPayloadByTriggerId };
};
